// @flow

import * as React from 'react';
import { useHoverDirty } from 'react-use';
import styled from 'styled-components';

import LoadingSpinner from '../loading_spinner';
import type { ButtonTheme } from './theme';
import {
  DangerButtonTheme,
  PrimaryButtonTheme,
  SecondaryButtonTheme,
} from './theme';
import { Grid } from '../../layouts/grid';
import { CommonButtonStyles } from './shared';
import ArrowDownIcon from '../../icons/arrow_down';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

type ButtonProps = {|
  /**
   * If true, the button is not clickable and the `onClick` will not fire.
   * Additionally, the button will appear at half opacity.
   */
  disabled?: boolean,
  /**
   * Fired when button is clicked.
   */
  onClick: (evt: SyntheticEvent<HTMLButtonElement>) => void | Promise<void>,
  /**
   * Contents of the button, must be text.
   */
  children: string,
  /**
   * A classname that is added to the button element if provided.
   */
  className?: string,
  /**
   * If true, a loading spinner is show in place of the button content.
   */
  loading?: boolean,
  /**
   * The icon to include with the button
   */
  icon?: React.AbstractComponent<any, any>,
  /**
   * A react component to use instead of a plain `<button>` tag.
   * Always use a component that renders a `<button>` tag, such as a `<MenuButton>`.
   */
  as?: string | React.AbstractComponent<any, any>,
  ...DataProvidesProps,
|};

const defaultButtonProps = {
  disabled: false,
  loading: false,
};

const HiddenText = styled.span`
  visibility: hidden;
`;

const AlignedLoadingSpinner = styled(LoadingSpinner).attrs({
  size: 'small',
})`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const ButtonWrapper = styled.button`
  ${CommonButtonStyles}

  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;

  transition: background 0.15s, border-color 0.15s, color 0.15s,
    box-shadow 0.15s;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  ${({ theme }) => `
    background: ${theme.default.backgroundColor};
    border: solid 1px ${theme.default.borderColor};
    color: ${theme.default.color};

    &:hover, &:focus {
      background: ${theme.hover.backgroundColor};
      border-color: ${theme.hover.borderColor};
      color: ${theme.hover.color};

      box-shadow: 0 2px 6px rgba(0, 0, 0, ${theme.hover.dropShadowOpacity});
    }

    &:active {
      background: ${theme.active.backgroundColor};
      border-color: ${theme.active.borderColor};
      color: ${theme.active.color};
    }
  `};

  &:active {
    box-shadow: none;
  }
`;

const EllipsifiedGrid = styled(Grid)`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconWrapper = styled.div`
  display: flex;
  margin-right: 4px;
`;
const ThemeableButton = (props: {
  ...ButtonProps,
  expandable?: boolean,
  theme: ButtonTheme,
}) => {
  const {
    children,
    icon,
    theme,
    disabled,
    onClick,
    className,
    loading,
    as,
    expandable,
  } = props;

  const buttonRef = React.useRef(null);
  const isHovering = useHoverDirty(buttonRef);

  const styledIcon = icon && (
    <IconWrapper>
      {React.createElement(icon, {
        color: isHovering ? theme.hover.iconColor : theme.default.iconColor,
        size: 'sm',
      })}
    </IconWrapper>
  );

  const childrenWithIcon = (
    <Grid container spacing={1} alignItems="center" justifyContent="center">
      {icon && styledIcon}
      <EllipsifiedGrid item xs title={children}>
        {children}
      </EllipsifiedGrid>
      {expandable && (
        <ArrowDownIcon
          color={isHovering ? theme.hover.iconColor : theme.default.iconColor}
        />
      )}
    </Grid>
  );

  const showLoading = loading && theme.loadingSpinnerFlavor;
  const buttonContent = showLoading ? (
    <>
      <AlignedLoadingSpinner
        data-provides="loading-spinner"
        flavor={theme.loadingSpinnerFlavor}
      />
      <HiddenText>{childrenWithIcon}</HiddenText>
    </>
  ) : (
    childrenWithIcon
  );

  return (
    <ButtonWrapper
      as={as}
      ref={buttonRef}
      theme={theme}
      disabled={disabled}
      aria-disabled={disabled}
      onClick={onClick}
      className={className}
      {...getDataProvidesProps(props)}
    >
      {buttonContent}
    </ButtonWrapper>
  );
};
ThemeableButton.defaultProps = defaultButtonProps;

const PrimaryButton = (props: ButtonProps) => (
  <ThemeableButton {...props} theme={PrimaryButtonTheme} />
);
PrimaryButton.defaultProps = {
  ...defaultButtonProps,
  'data-provides': 'primary-button',
};

const SecondaryButton = (props: ButtonProps) => (
  <ThemeableButton {...props} theme={SecondaryButtonTheme} />
);
SecondaryButton.displayName = 'SecondaryButton';
SecondaryButton.defaultProps = {
  ...defaultButtonProps,
  'data-provides': 'secondary-button',
};

const ExpandableButton = (props: ButtonProps) => (
  <ThemeableButton {...props} expandable theme={SecondaryButtonTheme} />
);

ExpandableButton.displayName = 'ExpandableButton';
ExpandableButton.defaultProps = {
  ...defaultButtonProps,
  'data-provides': 'expandable-button',
};

const DangerButton = (props: ButtonProps) => (
  <ThemeableButton {...props} theme={DangerButtonTheme} />
);

DangerButton.displayName = 'DangerButton';
DangerButton.defaultProps = {
  ...defaultButtonProps,
  'data-provides': 'danger-button',
};

export { PrimaryButton, SecondaryButton, DangerButton, ExpandableButton };
export type { ButtonProps };
